import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/pl';
import type { WorkflowInvoice } from '../../types/workflowInvoice';

// Owner decision 2026-10-13 („Zamówienia wstecz”): the recognition month of an
// invoice defaults to the month of its issue date, so a backdated purchase or
// sale lands in the month the accountant books it. The operator may still pick
// another month; the backend mirror is invoiceSettlementMath.defaultRecognitionMonth
// (month via businessMonthOf, Europe/Warsaw).
/** Display format of every recognition-month picker (same as the cost-invoice dialog). */
export const RECOGNITION_MONTH_FORMAT = 'MM.YYYY';

const DATE_ONLY = /^(\d{4})-(0[1-9]|1[0-2])-(\d{2})/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Month (YYYY-MM) of an invoice issue date (the DTO keeps it date-only). */
export function issueDateMonth(issueDate: string | null | undefined): string | null {
  const match = issueDate?.match(DATE_ONLY);
  return match ? `${match[1]}-${match[2]}` : null;
}

/** The issue-date month, or the current month when the invoice has no issue date yet. */
export function defaultRecognitionMonth(issueDate: string | null | undefined, today: Dayjs = dayjs()): string {
  return issueDateMonth(issueDate) ?? today.format('YYYY-MM');
}

/**
 * Draft month default. An operator-chosen month stays; otherwise the month
 * follows the issue date (also when the issue date is edited or read by OCR).
 */
export function withDefaultRecognitionMonth(invoice: WorkflowInvoice, chosenByOperator = false, today: Dayjs = dayjs()): WorkflowInvoice {
  if (invoice.status !== 'DRAFT') return invoice;
  if (chosenByOperator && invoice.settlementPeriod) return invoice;
  const month = defaultRecognitionMonth(invoice.issueDate, today);
  return invoice.settlementPeriod === month ? invoice : { ...invoice, settlementPeriod: month };
}

/** „wrzesień 2026” — the Polish standalone month name. */
export function recognitionMonthLabel(month: string): string {
  return MONTH.test(month) ? dayjs(`${month}-01`).locale('pl').format('MMMM YYYY') : month;
}

/** Labels for the non-blocking hint when the chosen month differs from the issue-date month. */
export function recognitionMonthMismatch(month: string | null | undefined, issueDate: string | null | undefined): { month: string; issueMonth: string } | null {
  const issueMonth = issueDateMonth(issueDate);
  if (!month || !MONTH.test(month) || !issueMonth || issueMonth === month) return null;
  return { month: recognitionMonthLabel(month), issueMonth: recognitionMonthLabel(issueMonth) };
}

/**
 * Goods receipt / PZ link after an approved purchase invoice: the catch-up
 * settles in the invoice's own recognition month, not today's. Without a
 * loaded month the field is omitted and the backend uses the invoice's month.
 */
export function receiptRecognitionMonth(invoice: Pick<WorkflowInvoice, 'settlementPeriod'>): { recognitionMonth?: string } {
  return invoice.settlementPeriod && MONTH.test(invoice.settlementPeriod) ? { recognitionMonth: invoice.settlementPeriod } : {};
}
